import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import './VerifyEmail.css';

const OTP_LENGTH = 6;

export default function ResetPassword() {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState(location.state?.email || '');
  const [otpDigits, setOtpDigits] = useState(Array(OTP_LENGTH).fill(''));
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const otpInputsRef = useRef([]);

  useEffect(() => {
    if (!email) {
      navigate('/forgot-password');
    }
  }, [email, navigate]);

  const otpValue = otpDigits.join('');

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccessMessage('');

    if (!email || !otpValue || otpValue.length < OTP_LENGTH) {
      setError('กรุณากรอกอีเมลและรหัส OTP ให้ครบถ้วน');
      return;
    }

    if (!newPassword || newPassword.length < 6) {
      setError('รหัสผ่านต้องมีอย่างน้อย 6 ตัวอักษร');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('รหัสผ่านไม่ตรงกัน');
      return;
    }

    setIsSubmitting(true);
    try {
      await api.post('/api/customer/reset-password', {
        email,
        otp: otpValue,
        newPassword
      });
      setSuccessMessage('เปลี่ยนรหัสผ่านสำเร็จ กำลังกลับไปหน้าเข้าสู่ระบบ...');
      setTimeout(() => {
        navigate('/login', { replace: true });
      }, 2000);
    } catch (err) {
      console.error('Reset password error:', err);
      const serverMessage = err.response?.data?.error || 'ไม่สามารถเปลี่ยนรหัสผ่านได้';
      setError(serverMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOtpChange = (index, value) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    setOtpDigits((prev) => {
      const next = [...prev];
      next[index] = digit;
      return next;
    });
    if (digit && index < OTP_LENGTH - 1) {
      otpInputsRef.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, event) => {
    if (event.key === 'Backspace' && !otpDigits[index] && index > 0) {
      setOtpDigits((prev) => {
        const next = [...prev];
        next[index - 1] = '';
        return next;
      });
      otpInputsRef.current[index - 1]?.focus();
      event.preventDefault();
    }
    if (event.key === 'ArrowLeft' && index > 0) {
      otpInputsRef.current[index - 1]?.focus();
      event.preventDefault();
    }
    if (event.key === 'ArrowRight' && index < OTP_LENGTH - 1) {
      otpInputsRef.current[index + 1]?.focus();
      event.preventDefault();
    }
  };

  const handleOtpPaste = (event) => {
    const pasted = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (!pasted) {
      return;
    }
    const digits = pasted.split('');
    setOtpDigits((prev) => {
      const next = [...prev];
      for (let i = 0; i < OTP_LENGTH; i += 1) {
        next[i] = digits[i] || '';
      }
      return next;
    });
    const focusIndex = Math.min(digits.length, OTP_LENGTH - 1);
    setTimeout(() => {
      otpInputsRef.current[focusIndex]?.focus();
    }, 0);
    event.preventDefault();
  };

  return (
    <div className="verify-email-page">
      <div className="verify-shell">
        <div className="verify-illustration" aria-hidden="true">
          <div className="verify-illustration__badge">
            <i className="bi bi-shield-lock" />
            Reset Password
          </div>
          <h1>รีเซ็ตรหัสผ่านของคุณ</h1>
          <p>
            กรอกรหัส OTP {OTP_LENGTH} หลักที่ส่งไปยังอีเมลของคุณ และตั้งรหัสผ่านใหม่
          </p>
        </div>

        <div className="verify-card">
          <header>
            <h2>รีเซ็ตรหัสผ่าน</h2>
            <p>กรอกรหัส OTP และรหัสผ่านใหม่</p>
          </header>

          {error && (
            <div className="verify-alert verify-alert--error" role="alert">
              <span>{error}</span>
              <button type="button" onClick={() => setError('')} aria-label="ปิดข้อความ">
                <i className="bi bi-x-lg" />
              </button>
            </div>
          )}

          {successMessage && (
            <div className="verify-alert verify-alert--success" role="status">
              {successMessage}
            </div>
          )}

          <form className="verify-form" onSubmit={handleSubmit}>
            <label>
              <span>อีเมล</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                autoComplete="email"
                disabled
              />
            </label>

            <div className="verify-otp-field">
              <div className="verify-otp-field__label">
                <span>รหัส OTP</span>
                <small>กรอกตัวเลข {OTP_LENGTH} หลัก</small>
              </div>
              <div className="verify-otp-inputs" onPaste={handleOtpPaste}>
                {otpDigits.map((digit, index) => (
                  <input
                    key={index}
                    ref={(element) => {
                      otpInputsRef.current[index] = element;
                    }}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(event) => handleOtpChange(index, event.target.value)}
                    onKeyDown={(event) => handleOtpKeyDown(index, event)}
                    aria-label={`รหัส OTP หลักที่ ${index + 1}`}
                    required
                    disabled={isSubmitting}
                  />
                ))}
              </div>
            </div>

            <label style={{ position: 'relative' }}>
              <span>รหัสผ่านใหม่</span>
              <input
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="กรอกรหัสผ่านใหม่ (อย่างน้อย 6 ตัวอักษร)"
                required
                minLength={6}
                autoComplete="new-password"
                disabled={isSubmitting}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '36px',
                  background: 'none',
                  border: 'none',
                  color: 'var(--color-text-muted)',
                  cursor: 'pointer',
                  fontSize: '1.1rem',
                  padding: '4px'
                }}
                aria-label={showPassword ? 'ซ่อนรหัสผ่าน' : 'แสดงรหัสผ่าน'}
              >
                <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`} />
              </button>
            </label>

            <label style={{ position: 'relative' }}>
              <span>ยืนยันรหัสผ่าน</span>
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="ยืนยันรหัสผ่านใหม่"
                required
                minLength={6}
                autoComplete="new-password"
                disabled={isSubmitting}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '36px',
                  background: 'none',
                  border: 'none',
                  color: 'var(--color-text-muted)',
                  cursor: 'pointer',
                  fontSize: '1.1rem',
                  padding: '4px'
                }}
                aria-label={showConfirmPassword ? 'ซ่อนรหัสผ่าน' : 'แสดงรหัสผ่าน'}
              >
                <i className={`bi ${showConfirmPassword ? 'bi-eye-slash' : 'bi-eye'}`} />
              </button>
            </label>

            <button type="submit" className="verify-submit" disabled={isSubmitting}>
              {isSubmitting ? 'กำลังเปลี่ยนรหัสผ่าน...' : 'เปลี่ยนรหัสผ่าน'}
            </button>
          </form>

          <footer className="verify-footer">
            <button
              type="button"
              className="verify-footer__button"
              onClick={() => navigate('/forgot-password')}
            >
              กลับไปหน้าขอรหัส OTP
            </button>
          </footer>
        </div>
      </div>
    </div>
  );
}

